import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { Box, Flex } from '@/ui';
import { PrintableChakraProvider } from '../../components/PrintableChakraProvider';
import { useDocumentContext } from '../../contexts/DocumentContext';
import { FONT_TEXT } from '../../css/customFonts';
import type { PageProps } from '../../types';
import { CalendarGrid } from './components/CalendarGrid';
import { Footer } from './components/Footer';
import { MobileDayDetails } from './components/MobileDayDetails';
import { PageHeader } from './components/PageHeader';
import type { PageData } from './types';
import { toMonthKey } from './util.lib';

export const Page = ({
  experience,
  data,
  pageIndex,
  isCurrentPage,
}: PageProps<PageData>) => {
  const { isPrintEnv } = useDocumentContext();
  const isMobile = useIsMobileBreakpoint();
  const showMobileDetails = !isPrintEnv && isMobile;
  // All planner views (interactive and printable) render on a neutral white
  // page; the block theme no longer paints the page background.
  const pageBackground = '#FFFFFF';

  return (
    <PrintableChakraProvider>
      <Box
        bg={pageBackground}
        className="page"
        color="#000000"
        data-current-page={isCurrentPage}
        data-measure={`page-${pageIndex}`}
        fontSize="1em"
        p={
          isPrintEnv ? undefined : { base: '0.5em', sm: '0.75em', md: '1.25em' }
        }
      >
        <Flex
          flexDirection="column"
          fontFamily={FONT_TEXT}
          h="100%"
          position="relative"
        >
          {data.months.map((monthData, monthIndex) => {
            const index = pageIndex + monthIndex;
            const monthKey = toMonthKey(monthData.month);

            return (
              <Flex
                bg={pageBackground}
                flexDirection="column"
                flexGrow={1}
                fontSize={isPrintEnv ? '1em' : '1em'}
                inset={0}
                key={monthKey}
                position={isPrintEnv ? 'absolute' : 'static'}
              >
                <PageHeader
                  experience={experience}
                  month={monthData.month}
                  pageIndex={index}
                  pageIndexForMonth={data.pageIndexForMonth}
                />

                <Box flexGrow={1} overflow="visible" position="relative">
                  <CalendarGrid
                    experience={experience}
                    monthData={monthData}
                    pageIndex={index}
                  />
                </Box>

                {showMobileDetails && (
                  <MobileDayDetails
                    experience={experience}
                    monthData={monthData}
                  />
                )}

                {isPrintEnv && (
                  <Footer
                    experience={experience}
                    measureKey={`page-${pageIndex}-footer-${monthKey}`}
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      </Box>
    </PrintableChakraProvider>
  );
};
